// Core
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Image,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Switch,
  useColorMode,
  useColorModeValue,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react';
import { SunIcon } from '@chakra-ui/icons';

// Assets
import { header } from 'routes.js';
import { apiService } from 'services/ApiService';
import { getInitials, user, formatDateWithSuffix, validateLoadMore, parseNotifyDate } from 'services/Utils';
import crystal from 'assets/img/details/crystal-3.png';
import { ReactComponent as MoonIcon } from 'assets/svg/moon.svg';
import { ReactComponent as NotificationCenterIcon } from 'assets/svg/notifications-center.svg';
import { ReactComponent as EmptyListNotifications } from 'assets/svg/empty-list-notifications.svg';
import { ReactComponent as InfoIcon } from 'assets/svg/notifications/info.svg';
import { ReactComponent as PromoIcon } from 'assets/svg/notifications/promo.svg';
import { ReactComponent as CashbackIcon } from 'assets/svg/notifications/bonus.svg';
import { ReactComponent as AffiliateCashbackIcon } from 'assets/svg/notifications/bonus.svg';
import { ReactComponent as BillingIcon } from 'assets/svg/notifications/billing.svg';
import { ReactComponent as SecurityIcon } from 'assets/svg/notifications/security.svg';
import { ReactComponent as GiveawaysIcon } from 'assets/svg/notifications/giveaways.svg';
import { ReactComponent as MysteryBoxIcon } from 'assets/svg/notifications/mystery-box.svg';
import { ReactComponent as ReviewIcon } from 'assets/svg/notifications/review.svg';
import { ReactComponent as ReceiptIcon } from 'assets/svg/notifications/receipt.svg';
import { ReactComponent as AchievementsIcon } from 'assets/svg/notifications/achievements.svg';
import { ReactComponent as ChallengeIcon } from 'assets/svg/notifications/achievements.svg';
import { ReactComponent as SupportIcon } from 'assets/svg/notifications/security.svg';
import { ReactComponent as GameIcon } from 'assets/svg/notifications/game.svg';
import { ReactComponent as MarketplaceIcon } from 'assets/svg/wallet/slider/marketplace.svg';
import profileSub from 'assets/img/profile-sub.png';
import emptyPlan from 'assets/img/empty-plan.png';
import { ReactComponent as Security2Icon } from 'assets/svg/security.svg';
import { ReactComponent as WalletIcon } from 'assets/svg/wallet.svg';
import { ReactComponent as TransactionHistoryIcon } from 'assets/svg/transaction-history.svg';
import { ReactComponent as LogoutIcon } from 'assets/svg/logout.svg';
import { ReactComponent as ReferrerIcon } from 'assets/svg/referrer.svg';

// Components
import {
  ArrowNavbarIcon,
  NotificationIcon,
  LoginIcon
} from 'components/Icons/Icons';
import SwitchTheme from './SwitchTheme';
import ActivePlanHeader from './components/ActivePlan';
import ActivePlansHeader from './components/ActivePlans';

export default function HeaderLinks(props) {
  const { variant, children, fixed, secondary, userData, logoutModal, ...rest } = props;

  const navbarBorderColor = useColorModeValue('blue.25', '#262D47');
  const iconMenuColor = useColorModeValue('blue.25', '#9FABCB');
  const viewProfileColor = useColorModeValue('gray.75', '#9FABCB');
  const textColor = useColorModeValue('#0E1633', 'white');
  const textActiveColor = useColorModeValue('#0497FD', '#0497FD');
  const borderColorNotify = useColorModeValue('1px solid #CBE7FF','1px solid #262D47');
  const colorDateNotify = useColorModeValue('#0E1633', '#9FABCB');
  const notifyViewed = useColorModeValue('2px solid #CBE7FF', '2px solid #0E1633');
  const bgUnSub = useColorModeValue('rgba(255, 255, 255, 0.40)','rgba(0, 0, 0, 0.40)');

  const { colorMode, toggleColorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const { isOpen, onOpen, onClose } = useDisclosure();

  const heightInActiveBlock = useBreakpointValue({ base: '100px', sm: 'auto' });
  const paddingBottomInActiveBlock = useBreakpointValue({ base: '0px', md: '20px', lg: '0px' });
  const widthNotifyList = useBreakpointValue({ base: 'max-content', sm: '96%', md: '100%' });
  const maxWidthNotifyList = useBreakpointValue({ base: '84%', sm: '100%' });

  const [isLoadingNotify, setIsLoadingNotify] = useState(false);
  const [isPanelLoading, setIsPanelLoading] = useState(false);
  
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const handleDropdownToggle = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const limit = 5;
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState([]);
  const [dataMeta, setDataMeta] = useState(); 
  const { isLoading, refetch: refetchNotifications } = useQuery({
      queryKey: ['notifications', offset],
      queryFn: async () => {
        if(!localStorage.getItem('auth')) { return true; }

        const response = await apiService.getNotifications({ limit, offset });
        if(offset === 0){
            setData(response.data);
        } else {
            setData([...data, ...response.data]);
        }
        setDataMeta(response.meta);
        setIsLoadingNotify(false);
        return true;
      },
      refetchOnWindowFocus: false
  });

  const loadMore = async () => {
      setOffset(offset + limit);
      setIsLoadingNotify(true);
  };

  const { data: notificationsCount, refetch: refetchNotificationsCount } = useQuery({
      queryKey: ['notifications-count'],
      queryFn: async () => {
        if(!localStorage.getItem('auth')) { return true; }

        const response = await apiService.getNotificationsCount();
        return response.data;
      },
      refetchOnWindowFocus: false
  });

  const handleReadAll = async () => {
    try {
      await apiService.readAllNotifications();
      refetchNotifications();
      refetchNotificationsCount();
      return;
    } catch(e){
      return;
    }
  };
  
	const goToCustomerPortal = useCallback(async () => {
		/*setIsPanelLoading(true);
		const response = await apiService.goToCustomerPortal();

		if (response.message === 'Success') {
			window.location.href = response.data;
		}
		setTimeout(() => {
			setIsPanelLoading(false);
		}, 2000);*/
    window.location.href = 'https://lmctgiveaway.com/loyalty-membership';
	}, []);

  const isMobile = useBreakpointValue({ base: true, sm: false });

  /*const { data: activePlans } = useQuery({
    queryKey: ['active-plans-header'],
    queryFn: async () => {
        if(userData){
          const response = await apiService.getSubscriptions({status: 'active'});
          return response.data;
        }
    },
    refetchOnWindowFocus: false
  });*/
  
  return (
    <>
      <Flex align="center" mt="-4px" display={{base: 'none', lg: 'flex' }}>
        { header?.map((item, index) => {
          const isDropdown = !!item.views;
          const isActive = location.pathname.includes(item.path);

          return isDropdown ? (
            <Menu variant="ghost" key={index} isOpen={openDropdown === index} onClose={() => setOpenDropdown(null)}>
              <MenuButton
                onClick={() => handleDropdownToggle(index)}
                mr="16px"
                color={openDropdown === index ? {textActiveColor} : {textColor}}
              >
                <Text display="inline-block" fontWeight="500" color={openDropdown === index ? {textActiveColor} : {textColor}}>{item.name}</Text>
                <ArrowNavbarIcon  color={openDropdown === index ? {textActiveColor} : {textColor}} fontSize="16px" transform={openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)'} ml="4px" />
              </MenuButton>
              <MenuList>
                {item.views.map((subItem, subIndex) => (
                  <MenuItem key={subIndex} p="10px 8px" fontSize="14px" fontWeight="400">
                    <NavLink to={`${subItem.layout}${subItem.path}`} style={{ width: '100%', display: 'flex', letterSpacing: '-0.4px' }} activeClassName="active">
                      {subItem.name}
                    </NavLink>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <Flex key={index} m="0px 12px 0 16px">
              <Link 
                as={NavLink} 
                to={`${item.layout}${item.path}`} 
                mr="12px" 
                style={{
                  color: isActive ? textActiveColor : textColor,
                  fontWeight: 500,
                  letterSpacing: '-0.4px !important'
                }}
              >
                {item.name}
              </Link>
            </Flex>
          );
        })}

        {/*<Flex m="0px 12px 0 16px">
          <a 
            href={'https://www.lmctplus.store'} 
            target="_blank"
            style={{
              color: textColor,
              fontWeight: 500,
              letterSpacing: '-0.4px !important'
            }} rel="noreferrer"
          >
            Shop
          </a>
        </Flex>*/}
      
      </Flex>

      <Box ms="auto" w={{ base: '100%', md: 'unset' }} gap="16px" alignItems="center">
        { user() ? (
          <Flex gap="12px" justifyContent="end">
            <Box display={{ base: 'none', md: 'block'}}><SwitchTheme /></Box>
            <Menu variant="notifications">
              <MenuButton className={ isMobile ? 'menu-header' : ''}>
                <NotificationIcon fontSize="24px" color={isDark ? '#9FABCB' : '#7787B0'} />
                { notificationsCount?.count > 0 && (
                    <Box className="new-notifications" />
                )}
              </MenuButton>
              <MenuList style={{ width: widthNotifyList, maxWidth: maxWidthNotifyList }}>
                <Flex borderBottom={borderColorNotify} justifyContent="space-between" pt="12px" pb="10px">
                  <Text variant="title-md" fontWeight="500">Notifications</Text>
                  <Text 
                    cursor="pointer"
                    onClick={handleReadAll} 
                    textAlign="right"
                    color={ notificationsCount?.count > 0 ? '#099BFF' : '#4C505C' }>
                      Mark All as Read
                    </Text>
                </Flex>

                { data?.length > 0 ? (
                  <Box h="423px" overflow="auto">
                    { data.map((item, index) => (
                      <Flex key={index} mt="16px" p="12px 8px" gap="8px" borderBottom={borderColorNotify}>
                        <Box p="4px" w="32px" h="32px" borderRadius="8px" bg="rgba(9, 155, 255, 0.10)" position="relative">
                          { item?.type === 'info' && (<InfoIcon fontSize="24px" />)}
                          { item?.type === 'promo' && (<PromoIcon fontSize="24px" />)}
                          { item?.type === 'billing' && (<BillingIcon fontSize="24px" />)}
                          { item?.type === 'security' && (<SecurityIcon fontSize="24px" />)}
                          { item?.type === 'giveaway' && (<GiveawaysIcon fontSize="24px" />)}
                          { item?.type === 'receipt' && (<ReceiptIcon fontSize="24px" />)}
                          { item?.type === 'achievement' && (<AchievementsIcon fontSize="24px" />)}
                          { item?.type === 'game' && (<GameIcon fontSize="24px" />)}
                          { item?.type === 'review' && (<ReviewIcon fontSize="24px" />)}
                          { item?.type === 'mysteryBox' && (<MysteryBoxIcon fontSize="24px" />)}
                          { item?.type === 'cashback' && (<CashbackIcon fontSize="24px" />)}
                          { item?.type === 'affiliateCashback' && (<AffiliateCashbackIcon fontSize="24px" />)}
                          { item?.type === 'challenge' && (<ChallengeIcon fontSize="24px" />)}
                          { item?.type === 'support' && (<SupportIcon fontSize="24px" />)}
                          { item?.type === 'marketplace' && (<MarketplaceIcon fontSize="24px" />)}
                          { !item?.viewed && ( 
                            <Box bg="#099BFF" border={notifyViewed} borderRadius="full" w="10px" h="10px" top="0" right="0px" position="absolute" />
                          )}
                        </Box>

                        <Box>
                          <Text variant="text-xs" color={colorDateNotify}>{parseNotifyDate(item?.createdAt)}</Text>
                          <Text variant="title-md" py="6px">{item?.title}</Text>
                          <Text 
                            fontSize="15px" 
                            maxW="310px" 
                            w="100%" 
                            overflow="hidden" 
                            display="-webkit-box" 
                            style={{
                              WebkitLineClamp: 2, 
                              WebkitBoxOrient: 'vertical',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item?.text}
                          </Text>

                        </Box>
                      </Flex>
                    ))}

                    { validateLoadMore(data, dataMeta) && (
                      <Button variant="ghost-md" isLoading={isLoadingNotify} maxW="220px" onClick={loadMore} m="24px auto !important" left="50%"
                            transform="translate(-50%, 0)">
                        Load More
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box 
                    textAlign="center" 
                    py="40px" 
                    maxW={{sm: '325px'}} 
                    left="50%" 
                    position="relative" 
                    transform="translate(-50%, 0px)">
                      <EmptyListNotifications style={{margin: '0 auto'}} />
                      <Text variant="title-md" fontSize="20px" mt="20px" pb="9px" letterSpacing="-0.4px">No Notifications</Text>
                      <Text>We’ll let you know when there will be something to update you</Text>
                  </Box>
                )}
              </MenuList>
            </Menu>

            <Menu variant="account" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
                <MenuButton className={ isMobile ? 'menu-header' : ''}>
                  { userData?.image?.length > 3 ? (
                    <Image src={userData?.image} w="44px" h="44px" objectFit="cover" borderRadius="full" />
                  ) : (
                    <Box className={userData?.gender === 'Male' ? 
                      'gender-male' : userData?.gender === 'Female' ? 
                      'gender-female' : userData?.gender === 'Transgender' ? 
                      'gender-other' : 'gender-none' } w="44px" maxW="100%" h="44px" borderRadius="full !important">
                      <Text pt="12.5px !important">{getInitials(userData?.firstName, userData?.lastName)}</Text>
                    </Box>
                  )}
                </MenuButton>
                <MenuList>
                  <Flex flexDirection="column">
                    <NavLink to="/account" style={{width: '100%', display: 'flex'}} onClick={onClose}>
                      <Flex gap="12px" p="8px" w="100%"
                        _hover={{background: isDark ? '#0D234D' : 'white' , borderRadius: '12px'}}>
                        <Box w="48px">
                          { userData?.image?.length > 3 ? (
                            <Image src={userData?.image} w="48px" h="48px" objectFit="cover" borderRadius="full" />
                          ) : (
                            <Box className={userData?.gender === 'Male' ? 
                              'gender-male' : userData?.gender === 'Female' ? 
                              'gender-female' : userData?.gender === 'Transgender' ? 
                              'gender-other' : 'gender-none' } maxW="100%" w="48px" h="48px" borderRadius="full">
                              <Text pt="14px !important">{getInitials(userData?.firstName, userData?.lastName)}</Text>
                            </Box>
                          )}
                        </Box>

                        <Box>
                          <Text variant="title-lg" minW="220px"
                            fontSize="20px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                            {userData?.firstName} {userData?.lastName}
                          </Text>
                          <Text color={viewProfileColor} mt="2px">View profile</Text>
                        </Box>
                      </Flex>
                    </NavLink>

                    <Box m="16px 0 24px 0" borderRadius="16px" position="relative" overflow="hidden">
                      <ActivePlanHeader userData={userData} onClose={onClose} />

                      { !userData?.activePlan && (
                         <Box
                          className="animation-header-profile-inactive"
                          borderRadius="16px"
                          w="calc(100% + 24px)"
                          h="calc(100% + 24px)"
                          zIndex="9"
                          py={{base: '20px', sm: '8px'}}
                          top="-12px"
                          left="-12px"
                          overflow="hidden"
                          position="absolute"
                          bg={bgUnSub}
                          backdropFilter="blur(6px)">
                          <Box m={{base: '0px auto 0 auto', sm: '10px auto 0 auto'}}  maxW="398px" px="20px" textAlign="center" zIndex="10" position="relative">
                            <Text variant="header" fontSize={{base: '18px'}}>Get Your Subscription Now!</Text>
                          </Box>

                          <Box m="6px auto 0 auto" maxW="124px" zIndex="10">
                            <Button 
                              isLoading={isPanelLoading} 
                              onClick={goToCustomerPortal} 
                              variant="button-md-g"
                              _hover={!isDark ? { color: 'black !important'} : {}  }
                              >Subscribe</Button>
                          </Box>

                          <Box className="animation-crystals" position="absolute" bottom="-25px" w="100%" 
                            left={{base: '-10%', lg: '0'}}
                            transform={{base: 'scale(0.8)', lg: 'scale(1)'}}>
                            <Image src={crystal} alt="crystal" position="absolute" left="-40px" bottom="4px" transform="rotate(14deg) scaleX(-1)" width="130px" />
                            <Image src={crystal} alt="crystal" position="absolute" left="10px" bottom="2px" transform="rotate(-3deg) scaleX(-1)" width="80px" zIndex="8" />
                          </Box>

                          <Box className="animation-crystals" position="absolute" bottom="-25px" w="100%" 
                            right={{base: '-10%', lg: '0'}}
                            transform={{base: 'scale(0.8) scaleX(-1)', lg: 'scale(1) scaleX(-1)'}}>
                            <Image src={crystal} alt="crystal" position="absolute" left="-40px" bottom="4px" transform="rotate(14deg) scaleX(-1)" width="130px" />
                            <Image src={crystal} alt="crystal" position="absolute" left="10px" bottom="2px" transform="rotate(-43deg) scaleX(-1)" width="80px" zIndex="8" />
                          </Box>

                        </Box>
                      )}
                    </Box>

                    <MenuItem>
                      <NavLink to="/account/notifications-center">
                        <Flex gap="16px" alignItems="center">
                          <Box borderRadius="8px" bg="#099BFF" p="4px" w="32px" h="32px">
                            <NotificationCenterIcon color={iconMenuColor} />
                          </Box>
                          <Text variant="title-md" fontWeight="500">Notifications Center</Text>
                        </Flex>
                      </NavLink>
                    </MenuItem>

                    <MenuItem>
                      <NavLink to="/account/security">
                        <Flex gap="16px" alignItems="center">
                          <Box borderRadius="8px" bg="#099BFF" p="4px" w="32px" h="32px">
                            <Security2Icon color={iconMenuColor} />
                          </Box>
                          <Text variant="title-md" fontWeight="500">Security</Text>
                        </Flex>
                      </NavLink>
                    </MenuItem>

                    <MenuItem>
                      <NavLink to="/account/wallet">
                        <Flex gap="16px" alignItems="center">
                          <Box borderRadius="8px" bg="#099BFF" p="4px" w="32px" h="32px">
                            <WalletIcon color={iconMenuColor} />
                          </Box>
                          <Text variant="title-md" fontWeight="500">Wallet</Text>
                        </Flex>
                      </NavLink>
                    </MenuItem>

                    <MenuItem>
                      <NavLink to="/account/transaction-history">
                        <Flex gap="16px" alignItems="center">
                          <Box borderRadius="8px" bg="#099BFF" p="4px" w="32px" h="32px">
                            <TransactionHistoryIcon color={iconMenuColor} />
                          </Box>
                          <Text variant="title-md" fontWeight="500">Transaction History</Text>
                        </Flex>
                      </NavLink>
                    </MenuItem>

                    { userData?.canRefer && 
                      <MenuItem>
                        <NavLink to="/referrer">
                          <Flex gap="16px" alignItems="center">
                            <Box borderRadius="8px" bg="#099BFF" p="4px" w="32px" h="32px">
                              <ReferrerIcon color={iconMenuColor} />
                            </Box>
                            <Text variant="title-md" fontWeight="500">Referral</Text>
                          </Flex>
                        </NavLink>
                      </MenuItem>
                    }

                    <Divider w="calc(100% - 12px)" my="5px" ml="6px" borderColor={navbarBorderColor} borderTopWidth="0.05em" borderBottomWidth="0"/>
                    
                    <MenuItem>
                      <Flex p="9px 14px" my="3px" w="100%" onClick={logoutModal} gap="16px" alignItems="center">
                        <Box borderRadius="8px" bg="#099BFF" p="4px" w="32px" h="32px">
                          <LogoutIcon color={iconMenuColor} />
                        </Box>
                        <Text variant="title-md" fontWeight="500">Logout</Text>
                      </Flex>
                    </MenuItem>
                  </Flex>
                </MenuList>
              </Menu>
          </Flex>
        ) : (
          <>
            <Flex gap="16px" display={{ base: 'none', md: 'flex'}}>
              <SwitchTheme />
              {/*<NavLink to="/auth/login">
                <Button variant="solid-md" w="140px">Login</Button>
              </NavLink>*/}
            </Flex>

            {/*<Box float="right" display={{ base: 'block', md: 'none'}}>
              <NavLink to="/auth/login">
                <Box 
                  w="44px" 
                  h="44px" 
                  p="10px"
                  borderRadius="full"
                  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                  boxShadow="0px 1px 22.4px 0px rgba(9, 155, 255, 0.60)">
                  <LoginIcon fontSize="24px" />
                </Box>
              </NavLink>
            </Box>*/}
          </>
        )}
      </Box>
    </>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
